import { Link } from "wouter";
import { Footer } from "@/components/layout/footer";
import { RouteSeo } from "@/components/seo/route-seo";
import { SITE_URL, benCareJsonLd } from "@/lib/seo";

const articlePath = "/articles/first-light-to-golden-hour-liquid-stretch";

const articleJsonLd = {
  "@type": "Article",
  "@id": `${SITE_URL}${articlePath}#article`,
  headline: "From First Light to Golden Hour: The 60-Formulation Journey Behind Liquid Stretch",
  author: {
    "@id": `${SITE_URL}/ben-care#person`,
  },
  datePublished: "2026-04-29",
  dateModified: "2026-04-29",
  about: [
    "Liquid Stretch",
    "Vermont hemp oil",
    "CBD to THC formulation",
    "stretching routine",
    "body-care ritual",
  ],
  mainEntityOfPage: `${SITE_URL}${articlePath}`,
};

export default function FormulationJourney() {
  return (
    <div className="min-h-screen bg-[#fff8e9] text-[#172016]">
      <RouteSeo
        title="From First Light to Golden Hour: The 60-Formulation Journey Behind Liquid Stretch"
        description="The Liquid Stretch journey from First Light Harvest to Golden Hour Harvest, through 60 formulation attempts and a Vermont lab sample handoff."
        path={articlePath}
        type="article"
        jsonLd={[benCareJsonLd, articleJsonLd]}
      />

      <main>
        <article className="container mx-auto px-6 py-16 md:py-24 max-w-4xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#315b35]">
            Liquid Stretch formulation journal
          </p>

          <h1 className="mt-5 font-serif text-4xl md:text-6xl leading-tight text-[#123820]">
            From First Light to Golden Hour: The 60-Formulation Journey Behind
            Liquid Stretch
          </h1>

          <p className="mt-6 text-sm text-[#5d624f]">
            By <Link href="/ben-care" className="underline">Ben Care</Link> for Liquid Stretch
          </p>

          <div className="prose prose-lg md:prose-xl mt-10 max-w-none prose-headings:font-serif prose-headings:text-[#123820] prose-a:text-[#315b35]">
            <p>
              Most of us know we should stretch, but rarely do we linger. What if
              stretching felt so good, you did not want to stop?
            </p>

            <p>
              That question became the center of our work with Liquid Stretch. We were
              not trying to make something loud, heavy, or distracting. We were trying
              to make something that helped the body feel more willing. More curious.
              More available to slow movement.
            </p>

            <p>So we took the challenge into formulation and kept going.</p>

            <p>
              First Light Harvest became the double-sized 4 oz launch special. Then
              one attempt became ten. Ten became thirty. Eventually, we worked through
              60 formulations, refining the recipe each step of the way: texture,
              absorption, balance, feel, and how the oil supported a stretching session
              from the first few minutes into deeper, slower work.
            </p>

            <p>The formula we kept coming back to became Golden Hour Harvest.</p>

            <p>
              For us, the 3:1 CBD-to-THC balance matters. CBD brings a steadier, more
              grounded character to the experience, while THC adds warmth and body
              awareness. At 3:1, the goal is not to overwhelm the stretch. It is to
              help stretching feel more fluid, more intuitive, and easier to stay with.
            </p>

            <p>
              Yesterday, that work became more than notes, formulation trials, and testing. I drove
              the sample to the lab myself: a small container labeled Vermont Forest and
              LiquidStretch.com, carrying the result of all those iterations into the
              next stage.
            </p>

            <p>
              That drive felt like a quiet milestone. Not a launch. Not a finish line.
              The handoff.
            </p>

            <p>
              Now the formulation is ready for lab testing and regulatory compliance.
              Once that work is complete, we are looking forward to sharing the
              difference with people who want stretching to feel less like a chore and
              more like something the body naturally wants to return to.
            </p>

            <p>
              Learn more about the sold-out First Light Harvest and join the Golden Hour readiness list on the{" "}
              <Link href="/" className="underline">Liquid Stretch homepage</Link>.
            </p>
          </div>
        </article>
      </main>

      <Footer variant="brand" />
    </div>
  );
}
